import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useUsersSearchQuery } from "../../views/approvals/hooks/useApprovalsQueries";

/**
 * UserSearchInput — debounced search-as-you-type user picker.
 * Queries GET /api/v1/users?q=... via useUsersSearchQuery.
 * Falls back to manual ID entry if API is unavailable.
 *
 * Widths are inline, not `w-full`: tailwind.config.js REPLACES theme.width
 * (outside `extend`) with a four-entry custom scale, so `w-full` compiles to
 * nothing and Tailwind drops it silently — the field and its dropdown rendered
 * at intrinsic width in every consumer. Same workaround RightSheet documents.
 */
const FULL = { width: "100%" };
const UserSearchInput = ({ value, onChange, placeholder, disabled }) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: users, isLoading, isError } = useUsersSearchQuery(debouncedSearch);

  const handleClickOutside = useCallback((e) => {
    if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const handleSelect = (user) => {
    const displayName = [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email;
    onChange({ user_id: String(user.id), display_name: displayName, email: user.email });
    setSearch(displayName);
    setOpen(false);
  };

  const displayValue = value?.display_name || value?.user_id || "";

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        className="p-2 border border-border rounded-ctrl text-sm focus:border-primary focus:outline-none"
        style={FULL}
        placeholder={placeholder || "Search user by name or email..."}
        value={open ? search : displayValue}
        onFocus={() => {
          setOpen(true);
          setSearch(displayValue);
        }}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        disabled={disabled}
      />
      {open && debouncedSearch.length >= 2 && (
        <div
          className="absolute z-50 mt-1 bg-card border border-card-bd rounded-card max-h-48 overflow-y-auto"
          style={FULL}
        >
          {isLoading && (
            <div className="p-3 text-sm text-text-3">Searching...</div>
          )}
          {isError && (
            <div className="p-3 text-sm text-text-3">
              Could not search users. Enter user ID manually.
              <input
                type="text"
                className="p-2 mt-2 border border-border rounded-ctrl text-sm focus:border-primary focus:outline-none"
                style={FULL}
                placeholder="User ID"
                onChange={(e) => {
                  onChange({ user_id: e.target.value, display_name: e.target.value });
                }}
              />
            </div>
          )}
          {!isLoading && !isError && users?.length === 0 && (
            <div className="p-3 text-sm text-text-3">No users found.</div>
          )}
          {!isLoading && !isError && users?.map((u) => {
            const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
            return (
              <button
                key={u.id}
                type="button"
                className="text-left px-3 py-2 hover:bg-surface-2 text-sm cursor-pointer"
                style={FULL}
                onClick={() => handleSelect(u)}
              >
                <span className="font-medium text-text">{name || u.email}</span>
                {name && <span className="text-text-3 ml-2">{u.email}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

UserSearchInput.propTypes = {
  value: PropTypes.shape({
    user_id: PropTypes.string,
    display_name: PropTypes.string,
    email: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

export default UserSearchInput;
